import { Capacitor } from '@capacitor/core';

declare global {
  const __APP_VERSION__: string;
  const __BUILD_TIME__: string;
  const __NATIVE__: boolean;
  /** Standalone sample-data build (`vite build --mode preview`): no server, hash URLs. */
  const __PREVIEW__: boolean;
  interface Window {
    __bootStart?: number;
  }
}

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform() as 'android' | 'ios' | 'web';
export const appVersion = __APP_VERSION__;
export const buildTime = __BUILD_TIME__;

export const prefersReducedMotion = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

type Impact = 'light' | 'medium' | 'heavy';
type Notice = 'success' | 'warning' | 'error';

/** Haptics on Android; silent everywhere else. */
export const haptics = {
  async tick() {
    if (!isNative) return;
    const { Haptics } = await import('@capacitor/haptics');
    await Haptics.selectionChanged().catch(() => {});
  },
  async impact(style: Impact = 'light') {
    if (!isNative) return;
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
    await Haptics.impact({ style: map[style] }).catch(() => {});
  },
  async notify(type: Notice) {
    if (!isNative) return;
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    const map = {
      success: NotificationType.Success,
      warning: NotificationType.Warning,
      error: NotificationType.Error,
    };
    await Haptics.notification({ type: map[type] }).catch(() => {});
  },
};

/** The device's own time zone. */
export const deviceTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export function copyText(text: string): Promise<boolean> {
  return navigator.clipboard
    .writeText(text)
    .then(() => true)
    .catch(() => false);
}
